import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface FiyatlamaAyari {
  aktifMi: boolean;
  aylikArtisOrani: number;
  kabulEdilenOrtalamaVadeGun: number;
}

const GayrimenkulFiyatlama: React.FC = () => {
  const [ayar, setAyar] = useState<FiyatlamaAyari>({ aktifMi: false, aylikArtisOrani: 0, kabulEdilenOrtalamaVadeGun: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAyar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/GayrimenkulFiyatlamaAyarlari');
      setAyar({
        aktifMi: !!res.data.aktifMi,
        aylikArtisOrani: res.data.aylikArtisOrani ?? 0,
        kabulEdilenOrtalamaVadeGun: res.data.kabulEdilenOrtalamaVadeGun ?? 0,
      });
    } catch {
      toast.error('Ayarlar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAyar(); }, [fetchAyar]);

  const handleKaydet = async () => {
    setSaving(true);
    try {
      await axiosInstance.post('/api/GayrimenkulFiyatlamaAyarlari/update/1', ayar);
      toast.success('Ayarlar kaydedildi.');
    } catch {
      toast.error('Kaydetme sırasında hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 6 };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
        <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', background: '#f4f6fb', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: '#1a2440', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="fas fa-chart-line" style={{ color: '#e67d21', fontSize: 18 }}></i>
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Gayrimenkul Fiyatlama</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            Vadeye bağlı fiyat artışı kuralları — Müşteri Sunumları'ndaki teklif fiyatlarına yansır
          </div>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 24, maxWidth: 640 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Durum</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Bu kural aktifleştirilmeden hiçbir hesaplama uygulanmaz.</div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: ayar.aktifMi ? '#059669' : '#94a3b8' }}>{ayar.aktifMi ? 'Aktif' : 'Pasif'}</span>
            <span
              onClick={() => setAyar(p => ({ ...p, aktifMi: !p.aktifMi }))}
              style={{ width: 42, height: 24, borderRadius: 12, background: ayar.aktifMi ? '#059669' : '#cbd5e1', position: 'relative', transition: 'background 0.15s ease', flexShrink: 0 }}
            >
              <span style={{ position: 'absolute', top: 2, left: ayar.aktifMi ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.15s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            </span>
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, opacity: ayar.aktifMi ? 1 : 0.5 }}>
          <div>
            <label style={lbl}>Aylık Artış Oranı (%)</label>
            <input
              type="number"
              step="0.01"
              min={0}
              disabled={!ayar.aktifMi}
              value={ayar.aylikArtisOrani}
              onChange={e => setAyar(p => ({ ...p, aylikArtisOrani: parseFloat(e.target.value) || 0 }))}
              style={inp}
              placeholder="ör: 2.5"
            />
          </div>
          <div>
            <label style={lbl}>Kabul Edilen Ortalama Vade (gün)</label>
            <input
              type="number"
              step="1"
              min={0}
              disabled={!ayar.aktifMi}
              value={ayar.kabulEdilenOrtalamaVadeGun}
              onChange={e => setAyar(p => ({ ...p, kabulEdilenOrtalamaVadeGun: parseInt(e.target.value) || 0 }))}
              style={inp}
              placeholder="ör: 90"
            />
          </div>
        </div>

        <div style={{ marginTop: 18, padding: '12px 14px', background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 10, fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
          <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
          Aktif edildiğinde: Müşteri Sunumları'nda bir teklif için ödeme planı hazırlanırken, planın ortalama vadesi
          yukarıdaki kabul edilen vadeyi aşarsa; aşan her ay için daire liste fiyatı üzerinden <b>Aylık Artış Oranı</b>
          kadar ek tutar hesaplanır ve önerilen teklif fiyatı olarak bilgilendirme amacıyla gösterilir.
        </div>

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleKaydet}
            disabled={saving}
            style={{ padding: '10px 22px', border: 'none', borderRadius: 9, background: saving ? '#94a3b8' : '#1a2440', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            <i className="fas fa-save" style={{ marginRight: 6 }}></i>{saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GayrimenkulFiyatlama;
